import React, { useState } from 'react';
import {
  Home,
  Building2,
  FileSearch,
  ClipboardList,
  Library,
  FileText,
  Pin,
  Upload,
  MessageSquare,
  Users,
  Wrench,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  GitBranch,
  Database,
  Settings,
  BarChart2
} from 'lucide-react';
import './Sidebar.css';

function Sidebar({ activeView, onNavigate, orgName }) {
  const [expandedSections, setExpandedSections] = useState({
    deals: true,
    diligence: false,
    doclib: false,
    utilities: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isActive = (view) => activeView === view;

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <img 
          src="https://wavcfonfn7v2m.ok.kimi.link/images/black-handshake.png" 
          alt="DealNexa" 
          className="sidebar-logo-img"
          style={{ height: '24px', width: 'auto' }}
        />
        <span className="sidebar-brand" title={orgName || 'DealNexa'}>{orgName || 'DealNexa'}</span>
      </div>

      <nav className="sidebar-nav">
        {/* Home */}
        <div
          className={`sidebar-item ${isActive('home') ? 'active' : ''}`}
          onClick={() => onNavigate('home')}
        >
          <span className={`sidebar-icon ${isActive('home') ? 'active' : ''}`}>
            <Home size={18} strokeWidth={1.5} />
          </span>
          <span className="sidebar-label">Home</span>
        </div>

        {/* Pipeline */}
        <div
          className={`sidebar-item ${isActive('pipeline') ? 'active' : ''}`}
          onClick={() => onNavigate('pipeline')}
        >
          <span className={`sidebar-icon ${isActive('pipeline') ? 'active' : ''}`}>
            <GitBranch size={18} strokeWidth={1.5} />
          </span>
          <span className="sidebar-label">Pipeline</span>
        </div>

        {/* Deals Section */}
        <div className="sidebar-section">
          <div 
            className={`sidebar-item sidebar-section-header ${isActive('deals') ? 'active' : ''}`}
            onClick={() => {
              onNavigate('deals');
              toggleSection('deals');
            }}
          >
            <span className={`sidebar-icon ${isActive('deals') ? 'active' : ''}`}>
              <Building2 size={18} strokeWidth={1.5} />
            </span>
            <span className="sidebar-label">Deals</span>
            <span 
              className="sidebar-chevron"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('deals');
              }}
            >
              {expandedSections.deals ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          </div>

          {expandedSections.deals && (
            <div className="sidebar-subitems">
              {/* Due Diligence */}
              <div className="sidebar-subsection">
                <div 
                  className={`sidebar-item sidebar-subheader ${isActive('aifindings') ? 'active' : ''}`}
                  onClick={() => {
                    onNavigate('aifindings');
                    toggleSection('diligence');
                  }}
                >
                  <span className={`sidebar-icon ${isActive('aifindings') ? 'active' : ''}`}>
                    <FileSearch size={16} strokeWidth={1.5} />
                  </span>
                  <span className="sidebar-label">Due Diligence</span>
                  <span 
                    className="sidebar-chevron"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection('diligence');
                    }}
                  >
                    {expandedSections.diligence ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </div>

                {expandedSections.diligence && (
                  <div className="sidebar-nested">
                    <div
                      className={`sidebar-item sidebar-nested-item ${isActive('findings') ? 'active' : ''}`}
                      onClick={() => onNavigate('findings')}
                    >
                      <span className="sidebar-icon">
                        <ClipboardList size={14} strokeWidth={1.5} />
                      </span>
                      <span className="sidebar-label">Findings Notes</span>
                    </div>

                    {/* Document Library under Due Diligence */}
                    <div className="sidebar-subsection">
                      <div 
                        className={`sidebar-item sidebar-nested-item ${isActive('library') ? 'active' : ''}`}
                        onClick={() => {
                          onNavigate('library');
                          toggleSection('doclib');
                        }}
                      >
                        <span className={`sidebar-icon ${isActive('library') ? 'active' : ''}`}>
                          <Library size={14} strokeWidth={1.5} />
                        </span>
                        <span className="sidebar-label">Document Library</span>
                        <span 
                          className="sidebar-chevron"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSection('doclib');
                          }}
                        >
                          {expandedSections.doclib ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </span>
                      </div>

                      {expandedSections.doclib && (
                        <div className="sidebar-deep-nested">
                          <div
                            className={`sidebar-item sidebar-deep-item ${isActive('summaries') ? 'active' : ''}`}
                            onClick={() => onNavigate('summaries')}
                          >
                            <span className="sidebar-icon">
                              <FileText size={14} strokeWidth={1.5} />
                            </span>
                            <span className="sidebar-label">Summaries</span>
                          </div>
                          <div
                            className={`sidebar-item sidebar-deep-item ${isActive('annotations') ? 'active' : ''}`}
                            onClick={() => onNavigate('annotations')}
                          >
                            <span className="sidebar-icon">
                              <Pin size={14} strokeWidth={1.5} />
                            </span>
                            <span className="sidebar-label">Annotations</span>
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>

              {/* Document Library shortcut */}
              <div
                className={`sidebar-item sidebar-subitem ${isActive('library') ? 'active' : ''}`}
                onClick={() => onNavigate('library')}
              >
                <span className={`sidebar-icon ${isActive('library') ? 'active' : ''}`}>
                  <Library size={16} strokeWidth={1.5} />
                </span>
                <span className="sidebar-label">Document Library</span>
              </div>

            </div>
          )}
        </div>

        {/* Utilities Section */}
        <div className="sidebar-section">
          <div 
            className="sidebar-item sidebar-section-header"
            onClick={() => toggleSection('utilities')}
          >
            <span className="sidebar-icon">
              <Wrench size={18} strokeWidth={1.5} />
            </span>
            <span className="sidebar-label">Utilities</span>
            <span className="sidebar-chevron">
              {expandedSections.utilities ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          </div>

          {expandedSections.utilities && (
            <div className="sidebar-subitems">
              <div
                className={`sidebar-item sidebar-subitem ${isActive('crm') ? 'active' : ''}`}
                onClick={() => onNavigate('crm')}
              >
                <span className={`sidebar-icon ${isActive('crm') ? 'active' : ''}`}>
                  <Database size={16} strokeWidth={1.5} />
                </span>
                <span className="sidebar-label">CRM</span>
              </div>
              <div
                className={`sidebar-item sidebar-subitem ${isActive('contacts') ? 'active' : ''}`}
                onClick={() => onNavigate('contacts')}
              >
                <span className={`sidebar-icon ${isActive('contacts') ? 'active' : ''}`}>
                  <Users size={16} strokeWidth={1.5} />
                </span>
                <span className="sidebar-label">Contacts</span>
              </div>
              <div
                className={`sidebar-item sidebar-subitem ${isActive('upload') ? 'active' : ''}`}
                onClick={() => onNavigate('upload')}
              >
                <span className={`sidebar-icon ${isActive('upload') ? 'active' : ''}`}>
                  <Upload size={16} strokeWidth={1.5} />
                </span>
                <span className="sidebar-label">Upload Documents</span>
              </div>
              <div
                className={`sidebar-item sidebar-subitem ${isActive('chats') ? 'active' : ''}`}
                onClick={() => onNavigate('chats')}
              >
                <span className={`sidebar-icon ${isActive('chats') ? 'active' : ''}`}>
                  <MessageSquare size={16} strokeWidth={1.5} />
                </span>
                <span className="sidebar-label">AI Chat</span>
              </div>
            </div>
          )}
        </div>

        {/* Deal Intelligence */}
        <div
          className={`sidebar-item ${isActive('analytics') ? 'active' : ''}`}
          onClick={() => onNavigate('analytics')}
        >
          <span className={`sidebar-icon ${isActive('analytics') ? 'active' : ''}`}>
            <BarChart2 size={18} strokeWidth={1.5} />
          </span>
          <span className="sidebar-label">Deal Intelligence</span>
        </div>
      </nav>

      <div className="sidebar-footer">
        <div
          className={`sidebar-footer-item ${activeView === 'settings' ? 'active' : ''}`}
          onClick={() => onNavigate('settings')}
          style={{ cursor: 'pointer' }}
        >
          <span className="sidebar-icon">
            <Settings size={18} strokeWidth={1.5} />
          </span>
          <span className="sidebar-label">Settings</span>
        </div>
        <div
          className={`sidebar-footer-item ${activeView === 'guide' ? 'active' : ''}`}
          onClick={() => onNavigate('guide')}
          style={{ cursor: 'pointer' }}
        >
          <span className="sidebar-icon">
            <HelpCircle size={18} strokeWidth={1.5} />
          </span>
          <span className="sidebar-label">Getting Started</span>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
